import { IAIStrategy } from '../AIStrategy';
import { ITile, PipValue, BoardEnd, IBoardTile } from '../../core/types';
import { DominoRules } from '../../core/DominoRules';
import { MediumStrategy } from './MediumStrategy';

/**
 * Hard AI Strategy: Uses a minimax-like approach or deep evaluation.
 * For simplicity in this implementation, it evaluates board control.
 */
export class HardStrategy implements IAIStrategy {
  name = 'hard';
  private mediumFallback = new MediumStrategy();

  selectTile(hand: ITile[], leftEnd: PipValue | null, rightEnd: PipValue | null, boardTiles: IBoardTile[]): { tile: ITile; end: BoardEnd } | null {
    const playable = DominoRules.getPlayableTiles(hand, leftEnd, rightEnd);
    if (playable.length === 0) return null;

    // For a robust hard strategy, evaluate options.
    let bestScore = -Infinity;
    let bestMove: { tile: ITile; end: BoardEnd } | null = null;

    for (const tile of playable) {
      const ends = DominoRules.getPlayableEnd(tile, leftEnd, rightEnd);
      for (const end of ends) {
        const score = this.evaluateMove(tile, end, hand, leftEnd, rightEnd);
        if (score > bestScore) {
          bestScore = score;
          bestMove = { tile, end };
        }
      }
    }

    if (!bestMove) {
      return this.mediumFallback.selectTile(hand, leftEnd, rightEnd, boardTiles);
    }

    return bestMove;
  }

  private evaluateMove(tile: ITile, end: BoardEnd, hand: ITile[], leftEnd: PipValue | null, rightEnd: PipValue | null): number {
    let score = tile.a + tile.b; // Prefer high pips to shed points
    if (DominoRules.isDouble(tile)) score += 5; // Bonus for doubles

    // Check how many tiles we can play after this move
    const newLeft = end === 'left' ? (tile.a === leftEnd ? tile.b : tile.a) : leftEnd;
    const newRight = end === 'right' ? (tile.b === rightEnd ? tile.a : tile.b) : rightEnd;
    
    const remainingHand = hand.filter(t => t.id !== tile.id);
    const futureOptions = DominoRules.getPlayableTiles(remainingHand, newLeft, newRight).length;
    
    score += futureOptions * 2; // Value future flexibility

    return score;
  }
}
